<template>
	<div class="ap" :class="rootClasses">
		<close-cross v-if="closeable" class="close-top-right-corner" :checkmark="close === 'check'" @click.prevent="doClose"></close-cross>
		<div class="ap-head">
			<template v-if="linkTitle(icon)">
				<template v-if="icon.link"><it-link :to="icon.link"><sym-icon :name="icon.icon"></sym-icon></it-link></template>
				<template v-else><object-link :src-type="icon.srcType" :src-sub-type="icon.srcSubType" :src-id="icon.srcId"><sym-icon :name="icon.icon"></sym-icon></object-link></template>
			</template>
			<template v-else><sym-icon :name="icon.icon"></sym-icon></template>
			<div class="ap-head-links">
				<template v-for="t in links">
					<template v-if="linkTitle(t)">
						<template v-if="t.link">
							<it-link :to="t.link" class="ap-title">{{ t.title }}</it-link>
						</template>
						<template v-else>
							<object-link :src-type="t.srcType" :src-sub-type="t.srcSubType" :src-id="t.srcId" class="ap-title">{{ t.title }}</object-link>
						</template>
					</template>
					<template v-else>
						<span class="ap-title">{{ t.title }}</span>
					</template>
				</template>
			</div>
		</div>
		<div class="ap-body"><slot></slot></div>
		<div v-if="hasButtons" class="ap-foot">
			<a href="#" v-for="btn of buttons" class="ap-button" :title="btn.tooltip" @click="buttonAction(btn)"><sym-icon v-if="btn.icon" :name="btn.icon"></sym-icon><span v-if="btn.title">{{ btn.title }}</span></a>
		</div>
	</div>
</template>

<script setup lang="ts">
import {ref, computed} from "vue";
import CloseCross from "@fd/editors/file/CloseCross.vue";
import ObjectLink from "@fd/helpers/ObjectLink.vue";
import {ActionPanelButton, ActionPanelLink} from "@t/ui/ActionPanel.ts";
import ItLink from "@/router/ItLink.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";

const props = withDefaults(defineProps<{
	close?: "no" | "cross" | "check",

	icon: ActionPanelLink,
	links: ActionPanelLink[],
	buttons?: ActionPanelButton[]
}>(), {
	close: "cross",
	buttons: () => []
});

const emit = defineEmits(['action', 'close']);

const closeable = computed<boolean>(() => props.close !== "no");
const hasButtons = computed<boolean>(() => props.buttons.length > 0);

const rootClasses = computed<string[]>(() => {
	const c:string[] = [];
	if (closeable.value) c.push("closeable");
	return c;
});

function linkTitle(t:ActionPanelLink) {
	return !!t.link || (t.srcType && t.srcId);
}

function buttonAction(btn:ActionPanelButton):void {
	emit('action', btn.action);
}

function doClose() {
	emit('close');
}
</script>

<style>
.ap {
	--link-fg: hsl(var(--button-hue) 100% var(--action-panel-link-fg-lightness));
	--link-fg-hover: hsl(var(--button-hue) 100% var(--action-panel-link-fg-hover-lightness));
	--link-fg-active: hsl(var(--button-hue) 100% var(--action-panel-link-fg-active-lightness));

	padding: 0;
	width: var(--sidebar-width-always);
	position: relative;

	display: inline-flex;
	flex-direction: column;
	background-color: hsl(var(--button-hue) var(--button-sat) var(--action-panel-bg-lightness));
	/* background-color: var(--bg); */
	color: hsl(var(--button-hue) var(--button-sat) var(--action-panel-fg-lightness));
	cursor: pointer;
	box-sizing: border-box;
	overflow-clip-margin: 8px;

	--ap-border: hsl(var(--button-hue) var(--button-sat) var(--action-panel-border-lightness));
	border: 1px solid var(--ap-border);
	box-shadow: var(--panel-shadow);
	border-radius: var(--menu-radius);
	pointer-events: auto;
}
.ap-head {
	padding: 8px 12px 0 12px;
	display: grid;
	align-items: baseline;
	grid-template-columns: 20px 1fr;	/* head is separated icon and links */
	column-gap: 4px;
}
.ap-head-links {
	display: flex;
	flex-direction: column;
	row-gap: 4px;
	font-size: var(--font-size-s);
	line-height: calc(var(--font-size-s) * 1.25);
	font-weight: 500;
}
.ap-body {
	padding: 8px 12px;
	position: relative;
	font-size: var(--font-size-s);

	&:empty {
		padding-top:0;
	}

	& .ap-comment-readonly,
	& textarea {
		width: calc(100% + 24px);
		margin-left: -12px;
		padding: 4px 12px;
		line-height: 15px;
		font-size: var(--font-size-s);
	}

	& textarea {
		display: block;
		border-top: 1px solid var(--ap-border);
		resize: vertical;
		min-height: var(--input-height);
		box-shadow:none;

		&:focus,
		&:focus-visible {
			box-shadow:none;
		}
	}
}
.ap-body:has(> textarea) {
	padding-bottom: 0;
}

.ap-foot {
	border-top: 1px solid var(--ap-border);
	height: 26px;
	/* background-color: white; */
	display: flex;
	overflow: hidden;
	border-bottom-left-radius: calc(var(--menu-radius) - 1px);
	border-bottom-right-radius: calc(var(--menu-radius) - 1px);

	& > a.ap-button {
		flex: 1;
		/* background-color: #FF33EE; */
		text-align: center;
		font-size: var(--font-size-s);
		display: flex;
		flex-direction: row;
		align-items: baseline;
		line-height: 25px;
		justify-content: center;
		column-gap: 4px;
		--link-fg: hsl(var(--button-hue) var(--button-sat) var(--action-panel-fg-lightness));
		--link-fg-hover: hsl(var(--button-hue) var(--button-sat) var(--action-panel-fg-lightness));
		--link-fg-active: hsl(var(--button-hue) var(--button-sat) var(--action-panel-fg-lightness));
		--link-bg: hsl(var(--button-hue) var(--button-sat) var(--action-panel-butt-lightness));
		--link-bg-hover: hsl(var(--button-hue) var(--button-sat) var(--action-panel-butt-hover-lightness));
		--link-bg-active: hsl(var(--button-hue) var(--button-sat) var(--action-panel-butt-active-lightness));
		user-select:none;
		-webkit-user-select:none;

		& > .fi {
			font-size: var(--font-size);
			line-height: 25px;
		}

		&:not(:last-child) {
			border-right: 1px solid var(--ap-border);
		}
	}
}

</style>